import React, { useState, useEffect } from 'react';
import { streamEmbedsService, StreamEmbed } from '../../services/stream-embeds.service';

export function StreamEmbedsPage() {
  const [embeds, setEmbeds] = useState<StreamEmbed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmbed, setEditingEmbed] = useState<StreamEmbed | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    embed_url: '',
    display_order: 0,
    active: true,
  });

  useEffect(() => {
    loadEmbeds();
  }, []);

  const loadEmbeds = async () => {
    try {
      setLoading(true);
      const data = await streamEmbedsService.getAll();
      setEmbeds(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los embeds');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (embed?: StreamEmbed) => {
    if (embed) {
      setEditingEmbed(embed);
      setFormData({
        name: embed.name,
        embed_url: embed.embed_url,
        display_order: embed.display_order ?? 0,
        active: embed.active ?? true,
      });
    } else {
      setEditingEmbed(null);
      setFormData({
        name: '',
        embed_url: '',
        display_order: embeds.length + 1,
        active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEmbed(null);
    setFormData({ name: '', embed_url: '', display_order: 0, active: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingEmbed) {
        await streamEmbedsService.update(editingEmbed.id, formData);
      } else {
        await streamEmbedsService.create(formData);
      }
      await loadEmbeds();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el embed');
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      await streamEmbedsService.toggleActive(id, !currentActive);
      await loadEmbeds();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar el estado');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Seguro que quieres eliminar este embed?')) return;
    try {
      await streamEmbedsService.delete(id);
      await loadEmbeds();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar el embed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Stream Embeds</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gestiona las transmisiones en vivo que se muestran en la landing
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Agregar Embed
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* List */}
      <div className="space-y-4 md:space-y-0">
        {embeds.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No hay embeds registrados. Agrega uno para comenzar.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {embeds.map((embed) => (
                <div key={embed.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <span className="text-sm font-bold text-violet-600 dark:text-violet-400">
                        {embed.display_order ?? 0}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white text-lg">
                        {embed.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                        {embed.embed_url}
                      </p>
                      <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <button
                          onClick={() => handleToggleActive(embed.id, embed.active ?? false)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            embed.active
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {embed.active ? 'Activo' : 'Inactivo'}
                        </button>
                        <button
                          onClick={() => setPreviewId(previewId === embed.id ? null : embed.id)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                        >
                          {previewId === embed.id ? 'Ocultar' : 'Preview'}
                        </button>
                        <button
                          onClick={() => handleOpenModal(embed)}
                          className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300 text-sm font-medium"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(embed.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                  {previewId === embed.id && (
                    <div className="mt-3 relative w-full" style={{ paddingBottom: '56.25%' }}>
                      <iframe
                        className="absolute top-0 left-0 w-full h-full rounded-lg"
                        src={embed.embed_url}
                        title={embed.name}
                        frameBorder="0"
                        allow="fullscreen; autoplay"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">URL</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {embeds.map((embed) => (
                    <React.Fragment key={embed.id}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{embed.display_order ?? 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900 dark:text-white">{embed.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400 truncate block max-w-xs">{embed.embed_url}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(embed.id, embed.active ?? false)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                              embed.active
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {embed.active ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                          <button
                            onClick={() => setPreviewId(previewId === embed.id ? null : embed.id)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          >
                            {previewId === embed.id ? 'Ocultar' : 'Preview'}
                          </button>
                          <button
                            onClick={() => handleOpenModal(embed)}
                            className="text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-300"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(embed.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                      {previewId === embed.id && (
                        <tr>
                          <td colSpan={5} className="px-6 py-4">
                            <div className="relative w-full max-w-2xl mx-auto" style={{ paddingBottom: '35%' }}>
                              <iframe
                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                src={embed.embed_url}
                                title={embed.name}
                                frameBorder="0"
                                allow="fullscreen; autoplay"
                                allowFullScreen
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {editingEmbed ? 'Editar Embed' : 'Nuevo Embed'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ej: Corsagoat"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL del Embed *
                      </label>
                      <input
                        type="url"
                        required
                        value={formData.embed_url}
                        onChange={(e) => setFormData({ ...formData, embed_url: e.target.value })}
                        placeholder="Ej: https://player.kick.com/corsagoat?autoplay=false"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        URL completa del player embed (Kick, YouTube, Twitch, etc.)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Orden *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={formData.display_order}
                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Los embeds con menor numero aparecen primero como tab
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="embed-active"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
                      />
                      <label htmlFor="embed-active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Embed activo
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    {editingEmbed ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
